import { useState, useRef } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthentication from "../../hooks/useAuthentication";
import {
  updateUserProfile,
  uploadProfilePhoto,
  uploadCoverPhoto,
} from "../../utility/httpActions/userActions";
import { resolveImageUri } from "../../utility/constants";
import ImageWithLoader from "../../common-components/ImageWithLoader";

const MAX_BIO = 300;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const EditProfileDialog = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { currentUser, setCurrentUser } = useAuthentication();
  const [bio, setBio] = useState(currentUser?.bio || "");
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    queryClient.invalidateQueries({ queryKey: ["publicUser"] });
  };

  const { mutateAsync: saveBio, isPending: bioSaving } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data) => {
      if (data?.user) setCurrentUser(data.user);
    },
  });

  const { mutateAsync: saveAvatar, isPending: avatarSaving } = useMutation({
    mutationFn: uploadProfilePhoto,
    onSuccess: (data) => {
      if (data?.user) setCurrentUser(data.user);
    },
  });

  const { mutateAsync: saveCover, isPending: coverSaving } = useMutation({
    mutationFn: uploadCoverPhoto,
    onSuccess: (data) => {
      if (data?.user) setCurrentUser(data.user);
    },
  });

  const isSaving = bioSaving || avatarSaving || coverSaving;

  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setSnackbar({ severity: "error", message: "Image must be under 5 MB." });
      return;
    }
    if (!file.type.startsWith("image/")) {
      setSnackbar({
        severity: "error",
        message: "Please select an image file.",
      });
      return;
    }
    const url = URL.createObjectURL(file);
    if (type === "avatar") {
      setAvatarFile(file);
      setAvatarPreview(url);
    } else {
      setCoverFile(file);
      setCoverPreview(url);
    }
  };

  const handleSave = async () => {
    try {
      const bioChanged = bio !== (currentUser?.bio || "");
      if (bioChanged) await saveBio({ bio });
      if (avatarFile) await saveAvatar(avatarFile);
      if (coverFile) await saveCover(coverFile);
      invalidate();
      handleClose();
    } catch {
      setSnackbar({
        severity: "error",
        message: "Failed to save. Please try again.",
      });
    }
  };

  const handleClose = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setAvatarFile(null);
    setCoverFile(null);
    setAvatarPreview(null);
    setCoverPreview(null);
    setBio(currentUser?.bio || "");
    onClose();
  };

  const existingCover = currentUser?.coverPhoto
    ? resolveImageUri(currentUser.coverPhoto)
    : null;
  const existingAvatar = currentUser?.profilePhoto
    ? resolveImageUri(currentUser.profilePhoto)
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={isSaving ? undefined : handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit profile</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          {/* Cover + avatar wrapper — no overflow hidden on this outer box */}
          <Box sx={{ position: "relative", mb: 6 }}>
            {/* Cover photo */}
            <Box
              sx={{
                width: "100%",
                height: 140,
                bgcolor: "#046287",
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {coverPreview || existingCover ? (
                <ImageWithLoader src={coverPreview || existingCover} alt="" />
              ) : (
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.25)",
                    fontWeight: 700,
                    fontSize: 48,
                  }}
                >
                  {(currentUser?.name || "U")[0].toUpperCase()}
                </Typography>
              )}
              <IconButton
                onClick={() => coverInputRef.current?.click()}
                disabled={isSaving}
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
                size="small"
              >
                <CameraAltOutlinedIcon fontSize="small" />
              </IconButton>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleFileSelect(e, "cover")}
              />
            </Box>

            {/* Avatar — positioned outside cover's overflow:hidden */}
            <Box
              sx={{
                position: "absolute",
                bottom: -32,
                left: 16,
                cursor: "pointer",
              }}
              onClick={() => !isSaving && avatarInputRef.current?.click()}
            >
              <Box sx={{ position: "relative", width: 72, height: 72 }}>
                <Avatar
                  src={avatarPreview || existingAvatar}
                  sx={{
                    width: 72,
                    height: 72,
                    border: "3px solid white",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                    bgcolor: "#046287",
                  }}
                >
                  {(currentUser?.name || "U")[0].toUpperCase()}
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    bgcolor: "rgba(0,0,0,0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.15s",
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <CameraAltOutlinedIcon
                    sx={{ color: "white", fontSize: 22 }}
                  />
                </Box>
              </Box>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleFileSelect(e, "avatar")}
              />
            </Box>
          </Box>

          {/* Bio */}
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Bio
          </Typography>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            placeholder="Tell people about yourself"
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, MAX_BIO))}
            inputProps={{ maxLength: MAX_BIO }}
            helperText={`${bio.length}/${MAX_BIO}`}
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            sx={{ bgcolor: "#046287", "&:hover": { bgcolor: "#035070" } }}
          >
            {isSaving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default EditProfileDialog;
